import React, { Component, createRef } from "react";
import * as d3 from "d3";
import noDataPlaceholder from "../../assets/img/nodata_available.svg";

export interface PieChartData {
  label: string;
  value: number; // percentage
  diff?: number | null;
}

interface Props {
  data: PieChartData[];
  width?: number;
}

class PiePercentageChart extends Component<Props> {
  private svgRef = createRef<SVGSVGElement>();
  private tooltipDiv: HTMLDivElement | null = null;
  private COLORS = ["#E7B612", "#F1D814", "#D0A23D", "#FFB550", "#FFB881", "#F59E0B", "#D97706"];

  componentDidMount() {
    this.ensureTooltipDiv();
    this.renderChart();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.data !== this.props.data) {
      this.renderChart();
    }
  }

      /** Create a reusable floating tooltip div once */
  private ensureTooltipDiv() {
    if (this.tooltipDiv && document.body.contains(this.tooltipDiv)) return;

    const div = document.createElement("div");
    div.style.position = "fixed";
    div.style.pointerEvents = "none";
    div.style.zIndex = "9999";
    div.style.background = "rgba(0,0,0,0.85)";
    div.style.color = "#fff";
    div.style.padding = "6px 8px";
    div.style.borderRadius = "6px";
    div.style.fontFamily = "Poppins, sans-serif";
    div.style.fontSize = "12px";
    div.style.lineHeight = "1.3";
    div.style.boxShadow = "0 4px 12px rgba(0,0,0,0.25)";
    div.style.opacity = "0";
    div.style.transform = "translate(-50%, -120%)";
    div.style.transition = "opacity 120ms ease-out";
    document.body.appendChild(div);
    this.tooltipDiv = div;
  }

  /** Truncate label and add ellipsis if longer than max */
  private truncateLabel = (text: string, max = 10) => {
    if (!text) return "";
    return text.length > max ? text.slice(0, max) + "…" : text;
  };

  /** Wrap text into multiple lines for SVG rendering */
  private wrapText = (text: string, maxChars = 15): string[] => {
    if (!text) return [];
    const words = text.split(" "); const lines: string[] = [];
    let currentLine = "";
    words.forEach((word) => {
      if ((currentLine + word).length <= maxChars) currentLine += (currentLine ? " " : "") + word;
      else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    if (currentLine) lines.push(currentLine);
    return lines;
  };

  /** Escape HTML for tooltip content safety */
  private escapeHtml(str: string) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  /** Show tooltip with (label, value) at mouse position */
  private showTooltip = (label: string, value: number, diff: number | null, evt: MouseEvent) => {
    if (!this.tooltipDiv) return;
    const arrow =
      diff === null || diff === 0
        ? ""
        : diff > 0
        ? "▲"
        : "▼";

    this.tooltipDiv.innerHTML = `
      <div style="font-weight:600; margin-bottom:2px;">${this.escapeHtml(
      label
    )}</div>
      <div style="opacity:0.9;">${value}% ${diff !== null ? `(${arrow}${Math.abs(diff)})` : ""}</div>
    `;
    this.tooltipDiv.style.opacity = "1";

    // Position near mouse; keep within viewport
    const padding = 10;
    const { clientX, clientY } = evt;
    const rect = this.tooltipDiv.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let x = clientX + 12;
    let y = clientY - 12;

    if (x + rect.width + padding > vw) x = vw - rect.width - padding;
    if (y + rect.height + padding > vh) y = vh - rect.height - padding;
    if (y < padding) y = padding;

    this.tooltipDiv.style.left = `${x}px`;
    this.tooltipDiv.style.top = `${y}px`;
  };
  
  /** Hide tooltip */
  private hideTooltip = () => {
    if (!this.tooltipDiv) return;
    this.tooltipDiv.style.opacity = "0";
  };

  renderChart = () => {
    const { data, width = 480 } = this.props;

    if (!data || !data.length) return;

    // Normalize incoming data
    const normalizedData = data.map((d: any) => ({
      label: d.label ?? d.wamp ?? "",
      value: d.value ?? d.cubic_share_p1 ?? 0,
      diff: d.cubic_share_diff ?? null
    }));

    const height = 200;
    // const radius = Math.min(width * 0.38, height) / 2;

    const hasDiff = normalizedData.some(d => d.diff !== null && d.diff !== undefined);
    const radius = hasDiff
      ? Math.min(width * 0.40, height) / 2   
      : Math.min(width * 0.40, height) / 2;  
    const svg = d3.select(this.svgRef.current);
    svg.selectAll("*").remove();
    svg
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMinYMin meet")
      .style("width", "100%")
      .style("height", "100%");

    const pieGroup = svg
      .append("g")
      .attr("transform", `translate(${width / 2 + 10}, ${height / 2 + 10})`);

    const pie = d3
      .pie<PieChartData>()
      .value(d => d.value)
      .sort(null);

    const arc = d3
      .arc<d3.PieArcDatum<PieChartData>>()
      .innerRadius(0) // FULL PIE (no gap)
      .outerRadius(radius);

    const outerArc = d3
      .arc<d3.PieArcDatum<PieChartData>>()
      .innerRadius(radius * 1.35)
      .outerRadius(radius * 1.35);
    
    const pieData = pie(normalizedData).sort((a, b) => {
      return (a.startAngle + a.endAngle) / 2 - (b.startAngle + b.endAngle) / 2;
    });

    pieGroup
      .selectAll("polyline")
      .data(pieData)
      .enter()
      .append("polyline")
      .attr("stroke", "#999")
      .attr("stroke-width", 1)
      .attr("fill", "none")
      .attr("points", function (d) {
        const posA = arc.centroid(d);        // slice
        const posB = outerArc.centroid(d);   // bend point
        const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        const posC = [
          radius * 1.3 * (midAngle < Math.PI ? 1 : -1), // push outward
          posB[1]
        ];
        return [posA, posB, posC]
          .map(p => p.join(","))
          .join(" ");
      });

    const label = pieGroup
      .selectAll(".label-group")
      .data(pieData)
      .enter()
      .append("g")
      .attr("class", "label-group")
      .attr("transform", function (d) {
        const pos = outerArc.centroid(d);
        const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        pos[0] = radius * 1.35 * (midAngle < Math.PI ? 1 : -1);
        return `translate(${pos})`;
      });


      label
      .append("text")
      .attr("class", "value-text")
      .style("font-size", "8px")
      .style("fill", "#555")
      .style("font-family", "Poppins, sans-serif")
      .attr("text-anchor", d => {
        const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        return midAngle < Math.PI ? "start" : "end";
      })
      .each((d, i, nodes) => {
        d3.select(nodes[i]).text(""); 
        this.wrapText(`${d.data.label}: ${d.data.value}%`).forEach((line, lineIndex) => {
          d3.select(nodes[i])
            .append("tspan")
            .attr("x", 0)
            .attr("dy", lineIndex === 0 ? 0 : "1.2em")
            .text(line);
        });
      });

label.each(function (d) {
    if (d.data.diff === null || d.data.diff === undefined) return;
    const g = d3.select(this);
    const text =
      d.data.diff === 0
        ? "● 0.00"
        : `${d.data.diff > 0 ? "▲" : "▼"} ${Math.abs(d.data.diff).toFixed(2)}`;

    const color =
      d.data.diff === 0
        ? "#6B7280"
        : d.data.diff > 0
        ? "#2E7D32"
        : "#C62828";

    const bg =
      d.data.diff === 0
        ? "#E5E7EB"
        : d.data.diff > 0
        ? "#C8E6C9"
        : "#FDECEA";

    const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;


    const pill = g
      .append("g")
      .attr("transform", midAngle < Math.PI
        ? `translate(15, 12)`   // right side
        : `translate(-15, 12)` // left side
      );

    const textNode = pill
      .append("text")
      .attr("text-anchor", "middle")
      .style("font-size", "8px")
      .style("fill", color)
      .text(text);

    const bbox = (textNode.node() as SVGTextElement).getBBox();

      pill
        .insert("rect", "text")
        .attr("x", bbox.x - 4)
        .attr("y", bbox.y - 1)
        .attr("width", bbox.width + 8)
        .attr("height", bbox.height + 2)
        .attr("rx", 6)
        .attr("fill", bg);
  });


    const hoverArc = d3
      .arc<d3.PieArcDatum<PieChartData>>()
      .innerRadius(0)
      .outerRadius(radius + 8);

    // DRAW PIE
    const paths = pieGroup
      .selectAll("path")
      .data(pie(normalizedData))
      .enter()
      .append("path")
      .attr("fill", (_, i) => this.COLORS[i % this.COLORS.length])
      .attr("d", d =>
        arc({
          ...d,
          startAngle: d.startAngle,
          endAngle: d.startAngle
        })!
      )
      .transition()
      .duration(800)
      .attrTween("d", function (d) {
        const interpolate = d3.interpolate(
          { startAngle: d.startAngle, endAngle: d.startAngle },
          d
        );
        return function (t) {
          return arc(interpolate(t))!;
        };
      });

    // HOVER ANIMATION
    pieGroup
      .selectAll("path")
      .on("mouseover", function (event, d:any) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr("d", hoverArc(d)!);
      })
      .on("mouseout", function (event, d:any) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr("d", arc(d)!);
      });

    // RIGHT SIDE LABELS (Evenly Distributed)
  //   const labelGroup = svg
  //     .append("g")
  //     .attr("transform", `translate(${radius * 2 + 40}, 50)`);

  //   const labelSpacing = height /8;

  //   const labelItems = labelGroup
  //     .selectAll(".legend-item")
  //     .data(normalizedData)
  //     .enter()
  //     .append("g")
  //     .attr("class", "legend-item")
  //     .attr("transform", (_, i) => `translate(0, ${i * labelSpacing})`);
  //   labelItems
  //     .append("circle")
  //     .attr("cx", 0)
  //     .attr("cy", 0)
  //     .attr("r", 6)
  //     .attr("fill", (_, i) => this.COLORS[i % this.COLORS.length]);
  //   // labelItems
  //   //   .append("text")
  //   //   .attr("x", 15)
  //   //   .attr("y", 3)
  //   //   .style("font-family", "Poppins, sans-serif")
  //   //   .style("font-weight", "400")
  //   //   .style("font-size", "9px")
  //   //   .style("color", "#333")
  //   //   .text(d => `${d.label}  ${d.value}%`);

  //   labelItems
  // .append("text")
  // .attr("x", 15)
  // .attr("y", 3)
  // .style("font-family", "Poppins, sans-serif")
  // .style("font-weight", "400")
  // .style("font-size", "9px")
  // .style("fill", "#333")
  // .text(d => `${this.truncateLabel(d.label, 14)}  ${d.value}%`)
  // .each(function (d:any) {
  //   const text = d3.select(this);
  //   text.append("title").text(`${d.label} ${d.value}%`);
  // })
  // .on("mouseover", (event: MouseEvent, d:any) => {
  //   this.showTooltip(d.label, d.value, d.diff ?? null, event);
  // })
  // .on("mousemove", (event: MouseEvent, d:any) => {
  //   this.showTooltip(d.label, d.value, d.diff ?? null, event);
  // })
  // .on("mouseout", () => {
  //   this.hideTooltip();
  // });
  //     const pillGroup = labelItems
  //     .append("g")
  //     .attr("transform", `translate(${width - (radius * 2 + 80)}, 0)`);


  //     pillGroup.each(function (d: any) {

  //     if (d.diff === null || d.diff === undefined) return;

  //     const g = d3.select(this);

  //     const text =
  //     d.diff === 0
  //     ? "● 0.00"
  //     : `${d.diff > 0 ? "▲" : "▼"} ${Math.abs(d.diff).toFixed(2)}`;

  //     const color =
  //     d.diff === 0 ? "#6B7280" :
  //     d.diff > 0 ? "#2E7D32" : "#C62828";

  //     const bg =
  //     d.diff === 0 ? "#E5E7EB" :
  //     d.diff > 0 ? "#C8E6C9" : "#FDECEA";

  //     const textNode = g
  //     .append("text")
  //     .attr("text-anchor", "middle")
  //     .style("font-family", "Poppins, sans-serif")
  //     .style("font-size", "9px")
  //     .style("font-weight", "400")
  //     .style("fill", color)
  //     .text(text);

  //     const bbox = (textNode.node() as SVGTextElement).getBBox();

  //     g.insert("rect", "text")
  //     .attr("x", bbox.x - 6)
  //     .attr("y", bbox.y - 2)
  //     .attr("width", bbox.width + 12)
  //     .attr("height", bbox.height + 4)
  //     .attr("rx", 6)
  //     .attr("fill", bg);

    // });
  };
  render() {
    const { data } = this.props;
    if (!data || !data.length) {
      return (
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 120,
          color: "#707070",
          fontSize: 14,
          fontWeight: 500
        }}>
          <img
            src={noDataPlaceholder}
            alt="No Data"
          />
        </div>
      );
    }
    return <svg ref={this.svgRef} />;
  }
}

export default PiePercentageChart;
